import axiosInstance from './axiosInstance';
import {
  CheckpointFilter,
  CreateCheckpointForm,
  UpdateCheckpointForm,
  CheckpointListResponse,
  CheckpointResponse,
  BulkCreateCheckpointForm,
  BulkCheckpointApiResponse,
  CheckpointStatsFilter,
  ProjectTypeStatsResponse,
  AssetStatsResponse,
  ArchiveCheckpointRequest,
  ArchivedCheckpointsFilter,
  ArchivedCheckpointsResponse,
} from '../types/checkpoint';

// ========== Checkpoint API Services ==========

export const checkpointService = {
  // Get all checkpoints with filtering and pagination
  getCheckpoints: async (filters: CheckpointFilter): Promise<CheckpointListResponse> => {
    const params = new URLSearchParams();

    if (filters.search) params.append('search', filters.search);
    if (filters.projectTypeId) params.append('projectTypeId', filters.projectTypeId);
    if (filters.assetId) params.append('assetId', filters.assetId);
    if (filters.frequency) params.append('frequency', filters.frequency);
    if (filters.status) params.append('status', filters.status);
    if (filters.page) params.append('page', filters.page.toString());
    if (filters.limit) params.append('limit', filters.limit.toString());
    if (filters.sortBy) params.append('sortBy', filters.sortBy);
    if (filters.sortOrder) params.append('sortOrder', filters.sortOrder);

    const response = await axiosInstance.get(`/checkpoints?${params.toString()}`);
    return response.data;
  },

  // Get checkpoint by ID
  getCheckpointById: async (id: string): Promise<CheckpointResponse> => {
    const response = await axiosInstance.get(`/checkpoints/${id}`);
    return response.data;
  },

  // Create new checkpoint
  createCheckpoint: async (data: CreateCheckpointForm): Promise<CheckpointResponse> => {
    const response = await axiosInstance.post('/checkpoints', data);
    return response.data;
  },

  // Create multiple checkpoints in bulk
  createBulkCheckpoints: async (data: BulkCreateCheckpointForm): Promise<BulkCheckpointApiResponse> => {
    const response = await axiosInstance.post('/checkpoints/bulk', data);
    return response.data;
  },

  // Alias for createBulkCheckpoints for backward compatibility
  createCheckpoints: async (data: BulkCreateCheckpointForm): Promise<BulkCheckpointApiResponse> => {
    const response = await axiosInstance.post('/checkpoints/bulk', data);
    return response.data;
  },

  // Update checkpoint
  updateCheckpoint: async (id: string, data: UpdateCheckpointForm): Promise<CheckpointResponse> => {
    const response = await axiosInstance.patch(`/checkpoints/${id}`, data);
    return response.data;
  },

  // Delete checkpoint
  deleteCheckpoint: async (id: string): Promise<{ success: boolean; message: string }> => {
    const response = await axiosInstance.delete(`/checkpoints/${id}`);
    return response.data;
  },

  // Toggle checkpoint status
  toggleCheckpointStatus: async (id: string): Promise<CheckpointResponse> => {
    const response = await axiosInstance.patch(`/checkpoints/${id}/toggle-status`);
    return response.data;
  },

  // Get checkpoints by project type and asset
  getCheckpointsByProjectAndAsset: async (projectTypeId: string, assetId: string): Promise<CheckpointListResponse> => {
    const response = await axiosInstance.get(`/checkpoints/project/${projectTypeId}/asset/${assetId}`);
    return response.data;
  },

  // Search checkpoints for autocomplete
  searchCheckpoints: async (params: { query: string; limit?: number }): Promise<{ success: boolean; data: any[] }> => {
    const searchParams = new URLSearchParams();
    searchParams.append('query', params.query);
    if (params.limit) searchParams.append('limit', params.limit.toString());

    const response = await axiosInstance.get(`/checkpoints/search?${searchParams.toString()}`);
    return response.data;
  },

  // Get project types for filter dropdown
  getProjectTypes: async (): Promise<{ success: boolean; data: any[] }> => {
    const response = await axiosInstance.get('/project-types');
    return response.data;
  },

  // Get assets by project type for filter dropdown
  getAssetsByProjectType: async (projectTypeId: string): Promise<{ success: boolean; data: any[] }> => {
    const response = await axiosInstance.get(`/assets/project-type/${projectTypeId}`);
    return response.data;
  },

  // Get project types statistics
  getProjectTypesStats: async (filter: CheckpointStatsFilter = {}): Promise<ProjectTypeStatsResponse> => {
    const params = new URLSearchParams();
    if (filter.status) params.append('status', filter.status);
    if (filter.projectTypeId) params.append('projectTypeId', filter.projectTypeId);

    const response = await axiosInstance.get(`/checkpoints/stats/project-types?${params.toString()}`);
    return response.data;
  },

  // Get assets statistics
  getAssetsStats: async (projectTypeId: string, filter: CheckpointStatsFilter = {}): Promise<AssetStatsResponse> => {
    const params = new URLSearchParams();
    params.append('projectTypeId', projectTypeId);
    if (filter.status) params.append('status', filter.status);

    const response = await axiosInstance.get(`/checkpoints/stats/assets?${params.toString()}`);
    return response.data;
  },

  // Archive a checkpoint (only if inactive)
  archiveCheckpoint: async (id: string, data: ArchiveCheckpointRequest): Promise<CheckpointResponse> => {
    const response = await axiosInstance.post(`/checkpoints/${id}/archive`, data);
    return response.data;
  },

  // Get archived checkpoints
  getArchivedCheckpoints: async (filters: ArchivedCheckpointsFilter = {}): Promise<ArchivedCheckpointsResponse> => {
    const params = new URLSearchParams();

    if (filters.page) params.append('page', filters.page.toString());
    if (filters.limit) params.append('limit', filters.limit.toString());
    if (filters.projectTypeId) params.append('projectTypeId', filters.projectTypeId);
    if (filters.assetId) params.append('assetId', filters.assetId);
    if (filters.frequency) params.append('frequency', filters.frequency);
    if (filters.search) params.append('search', filters.search);
    if (filters.sortBy) params.append('sortBy', filters.sortBy);
    if (filters.sortOrder) params.append('sortOrder', filters.sortOrder);

    const response = await axiosInstance.get(`/checkpoints/archived?${params.toString()}`);
    return response.data;
  },

  // ========== Project Asset Checkpoint API Services ==========

  // Get checkpoints for a specific project asset
  getProjectAssetCheckpoints: async (projectAssetId: string): Promise<any> => {
    const response = await axiosInstance.get(`/project-assets/${projectAssetId}/checkpoints`);
    return response.data;
  },

  // Create checkpoint for a project asset
  createProjectAssetCheckpoint: async (projectAssetId: string, data: any): Promise<any> => {
    const response = await axiosInstance.post(`/project-assets/${projectAssetId}/checkpoints`, data);
    return response.data;
  },

  // Update project asset checkpoint (frequency only)
  updateProjectAssetCheckpoint: async (checkpointId: string, data: {
    name?: string;
    frequency?: string;
    description?: string;
    requirement?: string;
    taskType?: ('Visual' | 'Hearing' | 'Manual')[];
    isImageRequired?: boolean;
    isInputRequired?: 'None' | 'Number' | 'Text' | 'Both';
    executionPhase?: string;
    executionTime?: string;
  }): Promise<any> => {
    const response = await axiosInstance.put(`/project-assets/checkpoints/${checkpointId}`, data);
    return response.data;
  },

  // Toggle project asset checkpoint status (activate/deactivate)
  toggleProjectAssetCheckpointStatus: async (checkpointId: string, status: 'active' | 'inactive'): Promise<any> => {
    const response = await axiosInstance.put(`/project-assets/checkpoints/${checkpointId}/status`, { status });
    return response.data;
  },
}; 